'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { CircleAlert } from 'lucide-react';
import service from '@Service';
import { cn } from '@Utils/cn';

const OrganizerSchema = z.object({
  name: z.string().min(2, 'Please enter at least 2 characters'),
  email: z
    .string()
    .email('Please enter a valid email')
    .optional()
    .or(z.literal('')),
  phone: z.string().optional().or(z.literal('')),
  website: z.string().optional().or(z.literal('')),
});

export type EventOrganizerFormValues = z.infer<typeof OrganizerSchema>;

type Props = {
  className?: string;
  defaultValues?: Partial<EventOrganizerFormValues>;
  submitLabel?: string;
  invalidateKey?: (string | number)[];
  onSuccess?: (result?: unknown) => void;
  formId?: string;
  hideInternalActions?: boolean;
  editId?: string;
};

export default function EventOrganizerForm({
  className,
  defaultValues,
  submitLabel,
  invalidateKey = ['organizers'],
  onSuccess,
  formId = 'event-organizer-form',
  hideInternalActions = false,
  editId,
}: Props) {
  const queryClient = useQueryClient();
  const isEditMode = !!editId;
  const resolvedSubmitLabel =
    submitLabel ?? (isEditMode ? 'Update organizer' : 'Create organizer');

  const form = useForm<EventOrganizerFormValues>({
    resolver: zodResolver(OrganizerSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      website: '',
      ...defaultValues,
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    reset,
    setError,
    formState: { isSubmitting },
  } = form;

  const mutation = useMutation({
    mutationFn: async (values: EventOrganizerFormValues) => {
      const payload = {
        name: values.name.trim(),
        email: values.email?.trim() || null,
        phone: values.phone?.trim() || null,
        website: values.website?.trim() || null,
      };
      if (isEditMode) {
        return service.updateOrganizer(editId, payload);
      }
      return service.createOrganizer(payload);
    },
    onSuccess: async (result) => {
      if (invalidateKey?.length) {
        await queryClient.invalidateQueries({ queryKey: invalidateKey });
      }
      onSuccess?.(result);
      toastSuccess(isEditMode ? 'Organizer updated' : 'Organizer created');
      if (!isEditMode) {
        reset({ name: '', email: '', phone: '', website: '' });
      }
    },
    onError: (err: any) => {
      setError('root', {
        type: 'server',
        message:
          err?.message ||
          `Something went wrong while ${isEditMode ? 'updating' : 'creating'} the organizer. Please try again.`,
      });
    },
  });

  const onSubmit = (values: EventOrganizerFormValues) => {
    mutation.mutate(values);
  };

  const pending = isSubmitting || mutation.isPending;

  return (
    <div className={cn('max-w-xl', className)}>
      <Form {...form}>
        <form
          id={formId}
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <FormField
            control={control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., John Smith" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="organizer@example.com"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone</FormLabel>
                <FormControl>
                  <Input placeholder="+1 (555) 123-4567" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="website"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Website</FormLabel>
                <FormControl>
                  <Input placeholder="https://example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {form.formState.errors.root?.message ? (
            <Alert variant="destructive" className="my-4">
              <CircleAlert />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                {form.formState.errors.root?.message}
              </AlertDescription>
            </Alert>
          ) : null}

          {!hideInternalActions && (
            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => reset()}
                disabled={pending}
              >
                Reset
              </Button>
              <Button type="submit" variant="primary" loading={pending}>
                {resolvedSubmitLabel}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </div>
  );
}
